import { BehaviorSubject, Observable, ReplaySubject, combineLatest, of, } from 'rxjs';
import { distinctUntilChanged, filter, switchMap } from 'rxjs/operators';
import { asString, LazyExecutor, paddedHex, runWithRetries, hexString, bufferFromSource, } from '../util/misc.js';
import { AsyncMulticastSubject } from '../util/rx.js';
import { FairServiceScheduler, monotonicTime } from '../util/sched.js';
import { TickClock } from '../util/tick-timer.js';
import { BleDeviceError } from './interface.js';
import { loraxCommandChar, loraxEventChar, loraxReplyChar, loraxService, loraxVersionChar, pikachuCharacteristicBase, } from './uuids.js';
export const pikaUuid = (charIndex) => pikachuCharacteristicBase + ('0000' + charIndex.toString(16)).slice(-4);
export const EPERM = 1;
export const ENOENT = 2;
export const EBADF = 9;
export const EEXIST = 17;
export const EINVAL = 22;
export const ENFILE = 23;
export const EFBIG = 27;
export const EPIPE = 32;
export const EPROTO = 71;
export const ENOSYS = 88;
export const ETIMEDOUT = 116;
export const EMSGSIZE = 122;
export const ENOTCONN = 128;
export const ENOTSUP = 134;
// nonstandard codes for internal error states
export const EHANDLEPRUNED = 65537;
export const EHANDLEREUSED = 65538;
export const loraxLogSelectionKeys = [
    'command',
    'replyOk',
    'replyData',
    'replyError',
    'event',
    'ackEvent',
];
const defaultLog = false;
const defaultLoraxLog = {
    command: defaultLog,
    replyOk: defaultLog,
    replyData: defaultLog,
    replyError: defaultLog,
    event: defaultLog,
    ackEvent: defaultLog,
};
const errNameMap = {
    [EPERM]: 'Permission denied',
    [ENOENT]: 'Path not found',
    [EBADF]: 'Bad file handle',
    [EEXIST]: 'File exists',
    [EINVAL]: 'Invalid value',
    [ENFILE]: 'Too many open files',
    [EFBIG]: 'File too large',
    [EPIPE]: 'Target is disconnected',
    [EPROTO]: 'Protocol error',
    [ENOSYS]: 'Function not implemented',
    [ETIMEDOUT]: 'Timeout',
    [EMSGSIZE]: 'Invalid data size',
    [ENOTCONN]: 'Not connected',
    [ENOTSUP]: 'Operation not supported',
    [EHANDLEPRUNED]: 'File handle pruned',
    [EHANDLEREUSED]: 'File handle reused',
};
export class LoraxError extends Error {
    code;
    static isInstance(thing) {
        return thing instanceof LoraxError;
    }
    constructor(code) {
        super(errNameMap[code] || `Unknown error ${code}`);
        this.code = code;
    }
}
/** Return the error code from a `LoraxError`, or `undefined` if `err` is not a `LoraxError` */
export const getLoraxErrorCode = (err) => LoraxError.isInstance(err) ? err.code : undefined;
/** Does `code` indicate an error that might be recoverable with a retry? */
export const isRetriableLoraxErrorCode = (code) => [EPROTO, ETIMEDOUT].includes(code);
/** Does `code` indicate an error resulting from a disconnection? */
export const isDisconnectedLoraxErrorCode = (code) => [EPIPE, ENOTCONN].includes(code);
const loraxOpcodes = {
    GetAccessSeed: 0x00,
    UnlockAccess: 0x01,
    GetLimits: 0x02,
    AckEvents: 0x03,
    ReadShort: 0x10,
    WriteShort: 0x11,
    StatShort: 0x12,
    Unlink: 0x13,
    FileOpen: 0x20,
    FileRead: 0x21,
    FileWrite: 0x22,
    FileWatch: 0x23,
    FileUnwatch: 0x24,
    FileStat: 0x25,
    FileClose: 0x26,
    PruneFileHandles: 0x27,
};
const closeLoraxFile = async (handle, path, runCommand) => {
    const cmdBody = new DataView(new ArrayBuffer(2));
    cmdBody.setUint16(0, handle, true);
    let attempts = 0;
    for (;;) {
        try {
            const { log } = await runCommand(loraxOpcodes.FileClose, cmdBody.buffer, `Close handle ${handle} (${path})`, 0, true);
            log(`Close handle ${handle} (${path}) OK`);
            break;
        }
        catch (err) {
            const code = getLoraxErrorCode(err);
            if (code === EBADF) {
                return; // file is already closed
            }
            ++attempts;
            if (!isRetriableLoraxErrorCode(getLoraxErrorCode(err)) || attempts >= 5) {
                throw err;
            }
        }
    }
};
export class LoraxFile {
    handle;
    path;
    runCommand;
    afterClosed;
    limits;
    watchState;
    constructor(handle, path, runCommand, afterClosed, limits) {
        this.handle = handle;
        this.path = path;
        this.runCommand = runCommand;
        this.afterClosed = afterClosed;
        this.limits = limits;
    }
    isOpenInt = true;
    /** Is the file still open? */
    get isOpen() {
        return this.isOpenInt;
    }
    /** Offset of the chunk being returned in watch events, or `undefined` if watch is not set up */
    get watchOffset() {
        return this.watchState?.offset;
    }
    /** Last value returned from watch event */
    get watchValue() {
        return this.watchState?.value;
    }
    /** Maximum length that can be written in one operation, given the Lorax MTU */
    get maxWriteLength() {
        return this.limits.maxPayload - 4;
    }
    /** Called by `LoraxClient` when the connection is closed */
    onDisconnect() {
        this.watchState?.subj.error(new LoraxError(EPIPE));
        this.watchState = undefined;
        this.isOpenInt = false;
    }
    /** Called by `LoraxClient` when a watch event is received */
    onWatchEvent(body) {
        if (this.watchState) {
            this.watchState.value = body;
            this.watchState.subj.next(body);
        }
    }
    /** Read a chunk from the file */
    async read(offset, size) {
        if (offset > 65535 || size > 255) {
            throw new LoraxError(EINVAL);
        }
        const cmdBody = new DataView(new ArrayBuffer(5));
        cmdBody.setUint16(0, this.handle, true);
        cmdBody.setUint16(2, offset, true);
        cmdBody.setUint8(4, size);
        const { body, log } = await this.runCommand(loraxOpcodes.FileRead, cmdBody.buffer, `Read handle ${this.handle} (${this.path}) ${size} bytes @ ${offset}`, size, false);
        log(`Read handle ${this.handle} (${this.path}) ${hexString(body)} @ ${offset}`);
        return body;
    }
    /** Write a chunk to the file */
    async write(offset, data) {
        if (offset > 65535) {
            throw new LoraxError(EINVAL);
        }
        const header = new Uint8Array(4);
        const headerView = new DataView(header.buffer);
        headerView.setUint16(0, this.handle, true);
        headerView.setUint16(2, offset, true);
        const { log } = await this.runCommand(loraxOpcodes.FileWrite, new Uint8Array([...header, ...new Uint8Array(data)]).buffer, `Write handle ${this.handle} (${this.path}) ${hexString(data)} @ ${offset}`, 0, true);
        log(`Write handle ${this.handle} (${this.path}) OK`);
    }
    /** Start watching the file, or modify the watch parameters */
    async watch(period, offset, size, ackTimeoutMsec = 500) {
        if (offset > 65535 || size > 255) {
            throw new LoraxError(EINVAL);
        }
        const cmdBody = new DataView(new ArrayBuffer(10));
        cmdBody.setUint16(0, this.handle, true);
        cmdBody.setUint16(2, period, true);
        cmdBody.setUint16(4, Math.round(ackTimeoutMsec / 1.25), true);
        cmdBody.setUint16(6, offset, true);
        cmdBody.setUint16(8, size, true);
        const prevWatchState = this.watchState;
        try {
            // Set up watch state before starting the watch, so we don't have a race between emplacement
            // and the first watch event. Also, use `ReplaySubject` to buffer values that arrive before
            // subscription occurs.
            this.watchState = {
                subj: this.watchState?.subj ?? new ReplaySubject(1),
                value: offset === this.watchState?.offset ? this.watchState.value : undefined,
                offset,
            };
            const { log } = await this.runCommand(loraxOpcodes.FileWatch, cmdBody.buffer, `Watch handle ${this.handle} (${this.path}) period ${period} ${size} bytes ` +
                `@ ${offset} ackTimeout ${ackTimeoutMsec} ms`, 0, true);
            log(`Watch handle ${this.handle} (${this.path}) OK`);
            return this.watchState.subj.asObservable();
        }
        catch (error) {
            this.watchState = prevWatchState;
            throw error;
        }
    }
    /** Stop watching the file */
    async unwatch() {
        const cmdBody = new DataView(new ArrayBuffer(2));
        cmdBody.setUint16(0, this.handle, true);
        const { log } = await this.runCommand(loraxOpcodes.FileUnwatch, cmdBody.buffer, `Unwatch handle ${this.handle} (${this.path})`, 0, true);
        log(`Unwatch handle ${this.handle} (${this.path}) OK`);
        this.watchState?.subj.complete();
        this.watchState = undefined;
    }
    /** Get the file's size */
    async stat() {
        const cmdBody = new DataView(new ArrayBuffer(2));
        cmdBody.setUint16(0, this.handle, true);
        const { body, log } = await this.runCommand(loraxOpcodes.FileStat, cmdBody.buffer, `Stat handle ${this.handle} (${this.path})`, 4, true);
        const v = new DataView(body);
        const size = v.getUint32(0, true);
        log(`Stat handle ${this.handle} (${this.path}) size ${size}`);
        return size;
    }
    /** Close the file */
    async close() {
        if (!this.isOpenInt) {
            return;
        }
        await closeLoraxFile(this.handle, this.path, this.runCommand);
        this.onClosed(undefined);
    }
    /** Clean up once the file is closed */
    onClosed(err) {
        if (!this.isOpenInt) {
            return;
        }
        if (err) {
            this.watchState?.subj.error(err);
        }
        else {
            this.watchState?.subj.complete();
        }
        this.isOpenInt = false;
        this.afterClosed();
    }
}
export const loraxEventHeaderSize = 3; // sequence + opcode
export const loraxEventAckRecordSize = 3;
export const loraxWatchEventOverhead = 2; // file handle
export const LoraxOpenFlags = {
    create: 1,
    exclude: 2,
    truncate: 4,
};
const tickPeriod = 50;
const encodeString = (str) => new TextEncoder().encode(str);
class LoraxClient {
    logger;
    targets;
    /** Timeouts in milliseconds */
    timeout = { command: 600, pruneHandle: 1200, lateReply: 3000 };
    log = defaultLoraxLog;
    sequence = 0;
    operations = new Map();
    lateReplyOperations = new Map();
    loraxVersion;
    handles = new Map();
    replySub;
    eventSub;
    limits;
    unackedEvents = new Array();
    unackedEventCountSubj = new BehaviorSubject(0);
    isConnectedSubj = new BehaviorSubject(false);
    isReadySubj = new BehaviorSubject(false);
    timeoutTicker = new TickClock(this.isReadySubj, tickPeriod);
    constructor(logger, targets) {
        this.logger = logger;
        this.targets = targets;
        this.limits = new LazyExecutor(this.getLimitsCmd);
    }
    get unackedEventCount() {
        return this.unackedEventCountSubj.value;
    }
    get unackedEventCountObs() {
        return this.unackedEventCountSubj.asObservable();
    }
    get isConnected() {
        return this.isConnectedSubj.value;
    }
    get isConnectedObs() {
        return this.isConnectedSubj.asObservable();
    }
    async onConnect() {
        this.replySub?.unsubscribe();
        this.eventSub?.unsubscribe();
        try {
            const versionBuf = await this.targets.readChar(loraxService, loraxVersionChar);
            if (versionBuf.byteLength !== 2) {
                throw new LoraxError(EPROTO);
            }
            const version = new DataView(versionBuf).getUint16(0, true);
            if (version > 255) {
                throw new Error(`Unsupported Lorax version ${version}`);
            }
            const replyObs = await this.targets.observeNotifications(loraxService, loraxReplyChar);
            const eventObs = await this.targets.observeNotifications(loraxService, loraxEventChar);
            this.replySub = replyObs.subscribe(this.onReply);
            this.eventSub = eventObs.subscribe(this.onEvent);
            this.loraxVersion = version;
            this.isReadySubj.next(true);
            await this.setupStickyPrune();
            this.isConnectedSubj.next(true);
        }
        catch (err) {
            this.replySub?.unsubscribe();
            this.eventSub?.unsubscribe();
            if (BleDeviceError.causeFromAny(err) === 'charNotFound') {
                this.logger.log('Lorax characteristic not found');
            }
            else {
                throw err;
            }
        }
    }
    onDisconnect() {
        this.replySub?.unsubscribe();
        this.replySub = undefined;
        this.eventSub?.unsubscribe();
        this.eventSub = undefined;
        this.isConnectedSubj.next(false);
        this.isReadySubj.next(false);
        this.unackedEvents = [];
        this.unackedEventCountSubj.next(0);
        for (const op of this.operations.values()) {
            void op.onError(new LoraxError(EPIPE));
        }
        this.operations = new Map();
        this.lateReplyOperations = new Map();
        for (const file of this.handles.values()) {
            file.onDisconnect();
        }
        this.handles = new Map();
        this.sequence = 0;
        this.loraxVersion = undefined;
        this.limits.reset('Disconnected');
    }
    teardown() {
        this.onDisconnect();
        this.timeoutTicker.teardown();
    }
    readShort = async (path, offset, size) => {
        const encodedPath = encodeString(path);
        const { body, log } = await this.runCommand(loraxOpcodes.ReadShort, new Uint8Array([offset & 0xff, offset >> 8, size & 0xff, size >> 8, ...encodedPath]), `ReadShort ${path} ${size} bytes @ ${offset}`, size, false);
        log(`ReadShort ${path} ${hexString(body)} @ ${offset}`);
        return body;
    };
    writeShort = async (path, offset, flags, value) => {
        const encodedPath = encodeString(path);
        const { body, log } = await this.runCommand(loraxOpcodes.WriteShort, new Uint8Array([
            offset & 0xff,
            offset >> 8,
            flags,
            ...encodedPath,
            0,
            ...new Uint8Array(value),
        ]), `WriteShort ${path} flags ${flags} ${hexString(value)} bytes @ ${offset}`, 0, true);
        if (body.byteLength) {
            throw new LoraxError(EPROTO);
        }
        log(`WriteShort ${path}`);
    };
    statShort = async (path) => {
        const encodedPath = encodeString(path);
        const { body, log } = await this.runCommand(loraxOpcodes.StatShort, encodedPath, `StatShort ${path}`, 4, true);
        log(`StatShort ${path} ${hexString(body)}`);
        const view = new DataView(body);
        return view.getUint32(0, true);
    };
    open = async (path, flags) => {
        const encodedPath = new TextEncoder().encode(path);
        const limits = await this.getLimits();
        let lateReplyHandler;
        if (this.loraxVersion === 0) {
            lateReplyHandler = async (body, log) => {
                // handle late replies
                const d = new DataView(body);
                const handle = d.getUint16(0, true);
                log(`Open ${path} returned handle ${handle} late, closing`);
                await closeLoraxFile(handle, path, this.runCommand);
            };
        }
        const { body, log } = await this.runCommand(loraxOpcodes.FileOpen, new Uint8Array([flags, ...encodedPath]), `Open ${path} flags ${flags}`, 2, true, lateReplyHandler);
        if (body.byteLength !== 2) {
            throw new LoraxError(EPROTO);
        }
        const d = new DataView(body);
        const handle = d.getUint16(0, true);
        log(`Open ${path} handle ${handle}`);
        const prevFile = this.handles.get(handle);
        if (prevFile) {
            this.logger.warn(`Lorax handle ${handle} was previously open as ${prevFile.path} and now used for ${path}, cleaning up`);
            prevFile.onClosed(new LoraxError(EHANDLEREUSED));
        }
        const file = new LoraxFile(handle, path, this.runCommand, () => {
            if (this.handles.get(handle) === file) {
                this.handles.delete(handle);
            }
        }, limits);
        this.handles.set(handle, file);
        return file;
    };
    getAccessSeed = async () => {
        const { body, log } = await this.runCommand(loraxOpcodes.GetAccessSeed, new Uint8Array(), 'GetAccessSeed', 16, true);
        log('GetAccessSeed OK');
        return body;
    };
    unlockAccess = async (key) => {
        const { log } = await this.runCommand(loraxOpcodes.UnlockAccess, key, `UnlockAccess`, 0, true);
        log('UnlockAccess OK');
    };
    setupStickyPrune = async () => {
        if (this.loraxVersion !== 0) {
            await runWithRetries({
                exec: () => this.pruneFileHandles(this.timeout.pruneHandle, true),
                onError: ({ err }) => {
                    if (isDisconnectedLoraxErrorCode(getLoraxErrorCode(err))) {
                        throw err;
                    }
                    return Promise.resolve();
                },
                maxRetries: 10,
            });
        }
    };
    pruneFileHandles = async (timeoutMsec, sticky) => {
        const timeoutBleTicks = Math.round(timeoutMsec / 1.25);
        const cmdBody = new ArrayBuffer(3);
        const cmdBodyView = new DataView(cmdBody);
        cmdBodyView.setUint8(0, sticky ? 1 : 0);
        cmdBodyView.setUint16(1, timeoutBleTicks, true);
        const { body, log } = await this.runCommand(loraxOpcodes.PruneFileHandles, cmdBody, `PruneFileHandles ${sticky ? 'sticky' : 'non-sticky'} ${timeoutBleTicks * 1.25} ms`, 0, true);
        log(`PruneFileHandles`);
        return body;
    };
    getLimits = () => this.limits.get();
    ackEvents = async () => {
        const limits = await this.getLimits();
        const maxEvents = Math.floor(limits.maxPayload / loraxEventAckRecordSize);
        const toAck = this.unackedEvents.slice(0, maxEvents);
        const cmdBody = new ArrayBuffer(toAck.length * loraxEventAckRecordSize);
        const cmdBodyView = new DataView(cmdBody);
        for (const [i, event] of toAck.entries()) {
            cmdBodyView.setUint16(i * loraxEventAckRecordSize, event.sequence, true);
            cmdBodyView.setUint8(i * loraxEventAckRecordSize + 2, event.opcode);
        }
        const { log } = await this.runCommand(loraxOpcodes.AckEvents, cmdBody, this.log.ackEvent ? `AckEvents ${toAck.map((e) => e.sequence).join(',')}` : undefined, 0, true);
        if (this.log.ackEvent) {
            log('AckEvents');
        }
        this.unackedEvents = this.unackedEvents.slice(toAck.length);
        this.unackedEventCountSubj.next(this.unackedEvents.length);
        return toAck.length;
    };
    runCommand = (opcode, body, logMsg, maxReplyLength, exactReplyLength, onLateReply) => {
        const limits = this.limits.getSync();
        if (limits && (body.byteLength > limits.maxPayload || maxReplyLength > limits.maxPayload)) {
            throw new LoraxError(EMSGSIZE);
        }
        const sequence = this.sequence;
        const seqStr = paddedHex(sequence, 4).toUpperCase();
        if (!this.replySub) {
            throw new LoraxError(ENOTCONN);
        }
        this.sequence = (this.sequence + 1) & 0xffff;
        const header = new Uint8Array(3);
        const headerView = new DataView(header.buffer);
        headerView.setUint16(0, sequence, true);
        headerView.setUint8(2, opcode);
        const bytes = new Uint8Array([...header, ...new Uint8Array(body)]);
        return new Promise((res, rej) => {
            const operation = {
                onComplete: (body, log) => {
                    this.operations.delete(sequence);
                    res({ body, log });
                    return Promise.resolve();
                },
                onError: (err) => {
                    this.operations.delete(sequence);
                    rej(err);
                    return Promise.resolve();
                },
                onLateReply,
                startMsec: monotonicTime(),
                expectedReplyLength: exactReplyLength ? maxReplyLength : undefined,
            };
            this.operations.set(sequence, operation);
            void this.timeoutTicker
                .getTimer(this.timeout.command / this.timeoutTicker.tickPeriod)
                .onExpired.then(() => {
                if (this.operations.get(sequence) === operation) {
                    if (this.log.replyError) {
                        this.logger.warn(`Lorax seq ${seqStr} timed out`);
                    }
                    this.operations.delete(sequence);
                    if (operation.onLateReply) {
                        this.lateReplyOperations.set(sequence, operation);
                        void this.timeoutTicker
                            .getTimer((this.timeout.lateReply - this.timeout.command) /
                            this.timeoutTicker.tickPeriod)
                            .onExpired.then(() => {
                            this.lateReplyOperations.delete(sequence);
                        });
                    }
                    rej(new LoraxError(ETIMEDOUT));
                }
            });
            if (this.log.command && logMsg) {
                this.logger.log(`Lorax → seq ${seqStr} ${logMsg}`);
            }
            this.targets
                .writeCharWithoutResponse(loraxService, loraxCommandChar, bytes)
                .catch((err) => {
                this.operations.delete(sequence);
                if (BleDeviceError.causeFromAny(err) === 'notConnected') {
                    rej(new LoraxError(ENOTCONN));
                }
                else {
                    rej(err);
                }
            });
        });
    };
    onReply = (value) => {
        try {
            const view = new DataView(value);
            const sequence = view.getUint16(0, true);
            const errCode = view.getUint8(2);
            const timelyOp = this.operations.get(sequence);
            const lateOp = this.lateReplyOperations.get(sequence);
            const op = timelyOp ?? lateOp;
            const payload = value.slice(3);
            const seqStr = paddedHex(sequence, 4).toUpperCase();
            if (!op) {
                this.logger.warn(`Received Lorax reply with unexpected sequence ${seqStr}: ${hexString(value)}`);
                return;
            }
            const elapsed = monotonicTime() - op.startMsec;
            const seqTime = `seq ${seqStr} +${elapsed.toFixed(1)}ms`;
            if (errCode) {
                if (this.log.replyError) {
                    this.logger.warn(`Lorax ← ${seqTime} ERR ${errCode} (${new LoraxError(errCode).message})`);
                }
                void timelyOp?.onError(new LoraxError(errCode));
            }
            else if (op.expectedReplyLength !== undefined &&
                payload.byteLength !== op.expectedReplyLength) {
                if (this.log.replyError) {
                    this.logger.warn(`Lorax ← ${seqTime} unexpected reply ${hexString(value)}`);
                }
                void timelyOp?.onError(new LoraxError(EPROTO));
            }
            else {
                const logComplete = (msg) => {
                    if (msg && this.log.replyData) {
                        this.logger.log(`Lorax ← ${seqTime} ${msg}`);
                    }
                    else if (!msg && this.log.replyOk) {
                        this.logger.log(`Lorax ← ${seqTime} OK`);
                    }
                };
                void timelyOp?.onComplete(payload, logComplete);
                void lateOp?.onLateReply(payload, logComplete);
            }
        }
        catch (error) {
            this.logger.error('Failed to process Lorax reply', { error, value: hexString(value) });
            throw error;
        }
    };
    onEvent = (event) => {
        try {
            const headerView = new DataView(event.slice(0, 3));
            const sequence = headerView.getUint16(0, true);
            const opcode = headerView.getUint8(2);
            const body = event.slice(3);
            this.unackedEvents.push({ sequence, opcode });
            this.unackedEventCountSubj.next(this.unackedEvents.length);
            switch (opcode) {
                case 0x00: {
                    const fileValues = [];
                    const bodyView = new DataView(body);
                    for (let view = bodyView; view.byteLength;) {
                        if (view.byteLength < 4) {
                            this.logger.warn('Truncated Lorax watch event', {
                                event: hexString(event),
                            });
                            break;
                        }
                        const handle = view.getUint16(0, true);
                        const dataLen = view.getUint8(2);
                        const value = view.buffer.slice(3, 3 + dataLen);
                        if (value.byteLength !== dataLen) {
                            this.logger.warn('Truncated Lorax watch event', {
                                event: hexString(event),
                            });
                            break;
                        }
                        const file = this.handles.get(handle);
                        fileValues.push({ handle, file, value });
                        view = new DataView(view.buffer.slice(3 + dataLen));
                    }
                    if (this.log.event) {
                        const fileLogs = fileValues
                            .map(({ handle, file, value }) => {
                            const path = file?.path ?? '?';
                            const hex = hexString(value);
                            return `hdl ${handle} (${path}) → ${hex}`;
                        })
                            .join(', ');
                        this.logger.log(`Lorax watch event seq ${sequence}: ${fileLogs}`);
                    }
                    const invalidHandles = [];
                    for (const fv of fileValues) {
                        if (fv.file) {
                            fv.file.onWatchEvent(fv.value);
                        }
                        else {
                            invalidHandles.push(fv.handle);
                        }
                    }
                    if (invalidHandles.length) {
                        this.logger.warn('Watch event received with invalid file handle(s)', invalidHandles);
                    }
                    break;
                }
                case 0x01:
                case 0x02: {
                    const handleType = opcode === 0x01 ? 'file' : 'directory';
                    if (body.byteLength % 2) {
                        this.logger.warn(`Truncated Lorax ${handleType} handle prune event`, {
                            event: hexString(event),
                        });
                        break;
                    }
                    const handles = new Uint16Array(body);
                    this.logger.warn(`Pruned ${handleType} handles ${handles.join(',')}`);
                    if (handleType === 'file') {
                        for (const handle of handles) {
                            const file = this.handles.get(handle);
                            if (file) {
                                this.logger.warn(`Cleaning up handle ${handle} (${file.path})`);
                                file.onClosed(new LoraxError(EHANDLEPRUNED));
                            }
                        }
                    }
                    break;
                }
            }
            if (this.log.event) {
                this.logger.log(`Lorax event seq ${sequence} opcode ${opcode} body ${hexString(body)}`);
            }
        }
        catch (error) {
            this.logger.error('Failed to process Lorax event', { error, value: hexString(event) });
            throw error;
        }
    };
    getLimitsCmd = () => runWithRetries({
        exec: async () => {
            const { body, log } = await this.runCommand(loraxOpcodes.GetLimits, new Uint8Array([]), `GetLimits`, 6, true);
            const d = new DataView(body);
            const limits = {
                maxPayload: d.getUint16(0, true),
                maxOpenFiles: d.getUint16(2, true),
                maxInFlightCommands: d.getUint16(4, true),
            };
            log(`GetLimits ${asString(limits)}`);
            return limits;
        },
        onError: ({ err }) => {
            if (isDisconnectedLoraxErrorCode(getLoraxErrorCode(err))) {
                throw err;
            }
            return Promise.resolve();
        },
        maxRetries: 10,
    });
}
const schedulerFreq = 1;
export class ConnectionManager {
    logger;
    log = defaultLog;
    scheduler; // Enforces one GATT operation at a time
    lorax;
    connectingSubj = new BehaviorSubject(undefined);
    deviceSubj = new BehaviorSubject(undefined);
    notifications = new Map();
    subscriptions = [];
    constructor(logger) {
        this.logger = logger;
        this.scheduler = new FairServiceScheduler();
        this.lorax = new LoraxClient(this.logger, this);
        this.subscriptions.push(this.deviceStateObs
            .pipe(filter((v) => v === 'disconnected'))
            .subscribe(this.onDisconnected));
    }
    /** Close all subscriptions */
    teardown() {
        this.lorax.teardown();
        this.subscriptions.forEach((sub) => sub.unsubscribe());
    }
    /** State of the connection; shows `connecting` while Lorax is setting up, even though the underlying GATT is online */
    get deviceState() {
        return this.connectingSubj.value ?? this.deviceSubj.value?.state ?? 'disconnected';
    }
    /** `Observable` giving the same value as `deviceState` */
    get deviceStateObs() {
        return combineLatest([this.connectingSubj, this.deviceSubj]).pipe(switchMap(([connecting, device]) => connecting
            ? of(connecting)
            : device
                ? device.stateObs
                : of('disconnected')), distinctUntilChanged());
    }
    /** Name advertised by the device */
    get deviceName() {
        return this.deviceSubj.value?.name;
    }
    /** Number of unacknowledged Lorax events */
    get loraxUnackedEventCount() {
        return this.lorax.unackedEventCount;
    }
    /** `Observable` of the number of unacknowledged Lorax events */
    get loraxUnackedEventCountObs() {
        return this.lorax.unackedEventCountObs;
    }
    /** Lorax timeout settings */
    get loraxTimeout() {
        return this.lorax.timeout;
    }
    set loraxTimeout(v) {
        this.lorax.timeout = v;
    }
    /** Lorax client log settings */
    get loraxClientLog() {
        return this.lorax.log;
    }
    set loraxClientLog(v) {
        this.lorax.log = v;
    }
    get isLoraxConnected() {
        return this.lorax.isConnected;
    }
    get isLoraxConnectedObs() {
        return this.lorax.isConnectedObs;
    }
    /** Read the Lorax connection limits */
    getLoraxLimits() {
        return this.lorax.getLimits();
    }
    /** Read the Lorax access seed */
    getLoraxAccessSeed() {
        return this.lorax.getAccessSeed();
    }
    /** Return a Lorax access key computed from a seed */
    loraxUnlockAccess(key) {
        return this.lorax.unlockAccess(key);
    }
    /** Read a GATT characteristic */
    readChar = async (svc, char, quiet = false) => this.logWrap(async () => {
        return await this.scheduler.queue(async () => {
            if (this.log) {
                this.logger.log('read', { svc, char });
            }
            const value = await this.connectedDevice.readChar(svc, char);
            if (this.log) {
                this.logger.log('read done', { svc, char, value: hexString(value) });
            }
            return value;
        }, schedulerFreq);
    }, 'GATT read', () => ({ svc, char }), quiet);
    /**
     * Check if a GATT characteristic can be written without response
     * (usually used to check for old Web Bluetooth implementations)
     */
    canWriteWithoutResponse = async (svc, char) => {
        return await this.connectedDevice.canWriteWithoutResponse(svc, char);
    };
    /** Write a GATT characteristic with response */
    writeCharWithResponse = async (svc, char, value) => this.logWrap(async () => {
        await this.scheduler.queue(async () => {
            if (this.log) {
                this.logger.log('writeWithResponse', {
                    svc,
                    char,
                    value: hexString(value),
                });
            }
            await this.connectedDevice.writeCharWithResponse(svc, char, 'buffer' in value ? value.buffer : value);
            if (this.log) {
                this.logger.log('writeWithResponse done', {
                    svc,
                    char,
                    value: hexString(value),
                });
            }
        }, schedulerFreq);
    }, 'GATT write-with-response', () => ({ svc, char, value: hexString(value) }));
    /** Write a GATT characteristic without response */
    writeCharWithoutResponse = async (svc, char, value) => this.logWrap(async () => {
        await this.scheduler.queue(async () => {
            if (this.log) {
                this.logger.log('writeWithoutResponse', {
                    svc,
                    char,
                    value: hexString(value),
                });
            }
            await this.connectedDevice.writeCharWithoutResponse(svc, char, 'buffer' in value ? value.buffer : value);
            if (this.log) {
                this.logger.log('writeWithoutResponse done', {
                    svc,
                    char,
                    value: hexString(value),
                });
            }
        }, schedulerFreq);
    }, 'GATT write-without-response', () => ({ svc, char, value: hexString(value) }));
    /** Get an `Observable` providing values notified for a GATT characteristic */
    observeNotifications = async (svc, char) => this.logWrap(async () => {
        const key = [svc, char].map((u) => encodeURIComponent(String(u))).join('/');
        const subject = this.notifications.get(key) ??
            this.createGattCharNotificationSubject(svc, char);
        this.notifications.set(key, subject);
        return await subject.get();
    }, 'Notification start', () => ({ svc, char }));
    /** Read a chunk from a Lorax file */
    loraxReadShort = async (path, offset, size) => this.logWrap(() => this.lorax.readShort(path, offset, size), 'Lorax read-short', () => ({ path, offset, size }));
    /** Write a chunk to a Lorax file; see Lorax protocol docs for `flags` */
    loraxWriteShort = async (path, value, offset = 0, flags = 0) => this.logWrap(() => this.lorax.writeShort(path, offset, flags, bufferFromSource(value)), 'Lorax write-short', () => ({ path, value: hexString(value), offset, flags }));
    /** Stat a Lorax file */
    loraxStatShort = async (path) => this.logWrap(() => this.lorax.statShort(path), 'Lorax stat-short', () => ({ path }));
    /** Open a Lorax file; see Lorax protocol docs for `flags` */
    loraxOpen = (path, flags = 0) => this.logWrap(() => this.lorax.open(path, flags), 'Lorax open', () => ({ path, flags }));
    /** Send acknowledgement for as many unacknowledged Lorax events as will fit into one packet */
    loraxAckEvents = () => this.logWrap(() => this.lorax.ackEvents(), 'Lorax ack-events', () => ({}));
    /** Call after GATT is connected and services have been discovered */
    connect = async (device) => {
        if (this.deviceSubj.value && this.deviceSubj.value.state !== 'disconnected') {
            throw new Error('Another device is already connected');
        }
        try {
            this.connectingSubj.next('connecting');
            this.deviceSubj.next(device);
            await this.lorax.onConnect();
            this.connectingSubj.next(undefined);
        }
        catch (err) {
            this.deviceSubj.next(undefined);
            this.connectingSubj.next(undefined);
            throw err;
        }
    };
    /** Start disconnecting the device */
    disconnect = async () => {
        await this.deviceSubj.value?.startDisconnect();
    };
    onDisconnected = () => {
        this.lorax.onDisconnect();
        this.notifications.clear();
    };
    get connectedDevice() {
        const dev = this.deviceSubj.value;
        if (dev?.state !== 'connected') {
            throw new BleDeviceError('notConnected');
        }
        return dev;
    }
    /**
     * Run a function with log output if it fails
     * @param exec Function to run
     * @param desc Description of the function for use in log messages
     * @param getInfo Function to get info for inclusion in the log (allows only doing time-consuming
     * operations when an error happens)
     * @param quiet Emit a `log` instead of an `error`
     */
    logWrap = async (exec, desc, getInfo, quiet = false) => {
        try {
            return await exec();
        }
        catch (error) {
            if (this.log) {
                if (quiet) {
                    this.logger.log(`${desc} failed.`, { ...getInfo(), error });
                }
                else {
                    this.logger.error(`${desc} failed.`, { ...getInfo(), error });
                }
            }
            throw error;
        }
    };
    createGattCharNotificationSubject(svc, char) {
        let maybeSubscriber;
        const subj = new AsyncMulticastSubject({
            start: async () => {
                const obs = await this.scheduler.queue(() => this.connectedDevice.startNotifications(svc, char), schedulerFreq);
                return new Observable((subscriber) => {
                    maybeSubscriber = subscriber;
                    const innerValueSub = obs.subscribe((value) => {
                        if (this.log) {
                            this.logger.log('notification', { svc, char, value: hexString(value) });
                        }
                        subscriber.next(value);
                    });
                    const innerDisconnectSub = this.deviceStateObs
                        .pipe(filter((v) => v === 'disconnected'))
                        .subscribe(() => {
                        subscriber.complete();
                    });
                    return () => {
                        innerValueSub.unsubscribe();
                        innerDisconnectSub.unsubscribe();
                    };
                });
            },
            stop: async () => {
                maybeSubscriber?.complete();
                await this.scheduler.queue(() => this.connectedDevice.stopNotifications(svc, char), schedulerFreq);
            },
            replay: false,
        });
        return subj;
    }
}
//# sourceMappingURL=connection-manager.js.map